import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';

import { useBusinessBookDemo } from 'src/services';
import { BOOK_DEMO_ORIGIN } from 'src/data/common';
import { isCustomModelEnabledForGenerationTool } from 'src/data/customModel';
import { CONTENT_TYPE } from 'src/data/contentType';
import { GENERATION_TOOL } from 'src/data/generationTool';
import Button from 'src/components/Button';
import { Popup } from 'src/components/Popup';
import { useCustomerHasAccessToPerformance } from 'src/store/customer';
import { BookDemoPanel } from './components/BookDemoPanel';
import { DatasetTabs } from './components/DatasetTabs';
import { AnywordDatasetPanel } from './views/AnywordDatasetPanel';
import { CustomModelPanel } from './views/CustomModelPanel';
import { useHandlers } from './useHandlers';
import { useViewState } from './useViewState';
import { useStyles } from './useStyles';
import { TABS } from './const';

export const ScoringMethodPopup = ({ open, contentType, generationTool, assetType, onSubmit }) => {
  const styles = useStyles();

  const { handleConfirm, handleCancel, handleTabChange, handleCustomDatasetsLoad } = useHandlers({
    onSubmit,
  });
  const { activeTab, error } = useViewState();
  const isCustomDatasetEnabled = isCustomModelEnabledForGenerationTool(generationTool);
  const customerHasAccessToPerformance = useCustomerHasAccessToPerformance();
  const { userAllowedToBookDemo, handleBusinessBookDemoClick } = useBusinessBookDemo(
    BOOK_DEMO_ORIGIN.scoringMethodPopup
  );

  useEffect(() => {
    if (isCustomDatasetEnabled) {
      handleCustomDatasetsLoad(generationTool);
    }
  }, [generationTool, isCustomDatasetEnabled, handleCustomDatasetsLoad]);

  return (
    <Popup
      open={open}
      classes={{
        paper: styles.paper,
      }}
      onClose={handleCancel}
    >
      <DialogTitle
        className={styles.dialogTitle}
        disableTypography
      >
        <Typography className={styles.title}>Data sets and custom models</Typography>
        <Typography className={styles.description}>
          Select from one of Anyword’s data sets or from one of your custom models that matches best
          with your copy.
        </Typography>
      </DialogTitle>

      <DialogContent
        className={cx(styles.dialogContent, {
          [styles.unsetHeight]: !customerHasAccessToPerformance,
        })}
      >
        <DatasetTabs
          currentTab={activeTab}
          onTabChange={handleTabChange}
        />

        <div
          role="tabpanel"
          hidden={activeTab !== TABS.anyword}
        >
          <AnywordDatasetPanel
            contentType={contentType}
            error={error}
          />
        </div>

        <div
          role="tabpanel"
          hidden={activeTab !== TABS.custom}
        >
          {customerHasAccessToPerformance ? (
            <CustomModelPanel
              onCreationPopupClose={handleCustomDatasetsLoad}
              error={error}
              generationTool={generationTool}
              assetType={assetType}
            />
          ) : (
            <BookDemoPanel
              onClick={handleBusinessBookDemoClick}
              userAllowedToBookDemo={userAllowedToBookDemo}
            />
          )}
        </div>
      </DialogContent>

      {!customerHasAccessToPerformance && activeTab === TABS.custom ? null : (
        <DialogActions className={styles.dialogActions}>
          <Button
            className={styles.buttonAction}
            onClick={handleCancel}
            variant="text"
            color="primary"
          >
            Cancel
          </Button>

          <Button
            className={styles.buttonAction}
            onClick={handleConfirm}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      )}
    </Popup>
  );
};

ScoringMethodPopup.propTypes = {
  open: PropTypes.bool,
  contentType: PropTypes.oneOf(Object.values(CONTENT_TYPE)).isRequired,
  generationTool: PropTypes.oneOf(Object.values(GENERATION_TOOL)).isRequired,
  onSubmit: PropTypes.func.isRequired,
};

ScoringMethodPopup.defaultProps = {
  open: false,
};
